import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { SelectField } from '../../components/ui/SelectField';
import { PageHeader } from '../../components/layout/PageHeader';
import { ApiError } from '../../lib/api';
import { commitRefusal } from '../../lib/crmOnboarding/commitRule';
import type { CrmOnboardingRun } from '../../types/crmOnboarding';
import { useCurrentUser } from '../auth';
import {
  confirmRegistryPreview,
  getCrmOnboardingStatus,
  listCrmOnboardingRuns,
  startPullNow,
  startRegistryPreview,
} from './api';
import { ClubHealthTable } from './ClubHealthTable';
import { ConfirmRegistrationDialog } from './ConfirmRegistrationDialog';
import { PullResultView } from './PullResultView';
import { RegistryPreviewView } from './RegistryPreviewView';

// Operational → CRM onboarding (/crm-onboarding, admin-only).
// Brief: _architect/briefs/2026-09-28-crm-onboarding-tool.md
//
// STAGES A + B: the health panel, register clubs (preview → confirm) and Pull
// now. Linking Kyck accounts arrives in stage C.
//
// ⚠️⚠️ THERE IS NO DATE PICKER HERE AND THERE MUST NEVER BE ONE. An explicit
// `--from` applies to EVERY club, not the new one, so "backfill this club
// further" would re-pull the whole fleet. A never-pulled club gets its 730-day
// first pull automatically — that IS the backfill. Pull now sends no body.
//
// ⚠️ REGISTERING IS TWO PHASES ON PURPOSE. The CLI's real safety is a person
// reading the configured group names before anything is written; a single
// button reporting "Done" would remove that while looking like an improvement.

const errText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);
const latest = (runs: CrmOnboardingRun[], kind: CrmOnboardingRun['kind']) => runs.find((r) => r.kind === kind) ?? null;

export function CrmOnboardingPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [brand, setBrand] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [pullError, setPullError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const isAdmin = me.role === 'admin';

  const status = useQuery({
    queryKey: ['crm-onboarding', 'status', brand],
    queryFn: () => getCrmOnboardingStatus(brand),
    enabled: isAdmin,
  });
  const runs = useQuery({
    queryKey: ['crm-onboarding', 'runs', brand],
    queryFn: () => listCrmOnboardingRuns(brand),
    enabled: isAdmin,
    refetchInterval: (q) => ((q.state.data?.runs ?? []).some((r) => r.status === 'running') ? 5000 : false),
  });

  // A finished commit or pull changes the health panel.
  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: ['crm-onboarding'] });
  };

  const preview = useMutation({
    mutationFn: () => startRegistryPreview(brand),
    onMutate: () => setRegisterError(null),
    onSuccess: refreshAll,
    onError: (err) => setRegisterError(errText(err, 'Could not start the preview.')),
  });
  const confirm = useMutation({
    mutationFn: (previewId: string) => confirmRegistryPreview(brand, previewId),
    onMutate: () => setRegisterError(null),
    onSuccess: refreshAll,
    onError: (err) => setRegisterError(errText(err, 'Could not start the registration.')),
    onSettled: () => setConfirming(false),
  });
  const pull = useMutation({
    mutationFn: () => startPullNow(brand),
    onMutate: () => setPullError(null),
    onSuccess: refreshAll,
    onError: (err) => setPullError(errText(err, 'Could not start the pull.')),
  });

  if (!isAdmin) {
    return <p className="text-sm text-slate-600">Only admins can use CRM onboarding.</p>;
  }

  const s = status.data?.status;
  const allRuns = runs.data?.runs ?? [];
  const brandKey = brand ?? s?.brandKey ?? '';
  const latestPreview = latest(allRuns, 'registry-preview');
  const latestCommit = latest(allRuns, 'registry-commit');
  const latestPull = latest(allRuns, 'pull');
  const previewRunning = latestPreview?.status === 'running';
  const commitRunning = latestCommit?.status === 'running';
  const pullRunning = latestPull?.status === 'running';
  // The newer of the latest preview and the latest commit is what the section shows.
  const commitIsNewer =
    latestCommit !== null && (latestPreview === null || latestCommit.confirmsPreviewId === latestPreview.id);
  const refusal = latestPreview ? commitRefusal(latestPreview, brandKey, latestPreview.id) : null;
  const alreadyConfirmed = latestCommit?.confirmsPreviewId === latestPreview?.id && latestPreview !== null;

  return (
    <div className="space-y-10">
      <PageHeader
        title="CRM onboarding"
        description="Bring a new club's GymSales data into Kyck reporting: register it, let the pull backfill it, then link its Kyck account."
      />

      {s && s.brands.length > 1 && (
        <div className="max-w-xs">
          <SelectField
            aria-label="CRM brand"
            value={brandKey}
            onValueChange={setBrand}
            options={s.brands.map((b) => ({ value: b, label: b }))}
          />
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">Registered clubs</h2>
        {status.isLoading ? (
          <CenteredSpinner />
        ) : status.error || !s ? (
          <p className="text-sm text-red-600">{errText(status.error, 'Couldn’t load the CRM registry.')}</p>
        ) : (
          <ClubHealthTable status={s} />
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">1 · Register clubs</h2>
        <p className="max-w-3xl text-sm text-slate-700">
          First a preview: it reads the configured GymSales company groups and lists every club they would add, and
          writes nothing. Read the group names it shows. Only then confirm, which writes the registry.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => preview.mutate()}
            disabled={preview.isPending || previewRunning || commitRunning}
          >
            {previewRunning ? 'Preview running…' : 'Preview registration'}
          </Button>
          {latestPreview && !alreadyConfirmed && refusal === null && (
            <Button onClick={() => setConfirming(true)} disabled={confirm.isPending || commitRunning}>
              Confirm and register…
            </Button>
          )}
        </div>
        {latestPreview && !alreadyConfirmed && refusal !== null && latestPreview.status !== 'running' && (
          <p className="text-sm text-amber-800">Can’t confirm this preview: {refusal}</p>
        )}
        {registerError && <p className="text-sm text-red-600">{registerError}</p>}
        {commitIsNewer && latestCommit ? (
          <div className="rounded-md border border-slate-200 p-4">
            <RegistryPreviewView run={latestCommit} registered={s?.clubs ?? []} />
          </div>
        ) : (
          latestPreview && (
            <div className="rounded-md border border-slate-200 p-4">
              <RegistryPreviewView run={latestPreview} registered={s?.clubs ?? []} />
            </div>
          )
        )}
        {latestPreview && latestPreview.result && (
          <ConfirmRegistrationDialog
            preview={latestPreview}
            open={confirming}
            pending={confirm.isPending}
            onCancel={() => setConfirming(false)}
            onConfirm={() => confirm.mutate(latestPreview.id)}
          />
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-slate-900">2 · Pull now</h2>
        <p className="max-w-3xl text-sm text-slate-700">
          Runs the same pull as the nightly sync: the last 7 days for every club, and a {s?.firstPullDays ?? 730}-day
          first pull for any club never pulled before — at most {s?.maxFirstPullsPerRun ?? 5} of those per run.
          There is no date to choose: a new club’s history comes from its first pull.
        </p>
        <div>
          <Button onClick={() => pull.mutate()} disabled={pull.isPending || pullRunning || commitRunning}>
            {pullRunning ? 'Pull running…' : 'Pull now'}
          </Button>
        </div>
        {pullError && <p className="text-sm text-red-600">{pullError}</p>}
        {latestPull && (
          <div className="rounded-md border border-slate-200 p-4">
            <PullResultView run={latestPull} />
          </div>
        )}
      </section>

      <p className="text-xs text-slate-500">
        Linking a club to its Kyck account is not on this page yet — set the CRM ID in the account’s edit form.
      </p>
    </div>
  );
}
